import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { ThumbsUp, Loader2, ThumbsDown, Eye, X, CheckCircle, XCircle, Target, Activity, Clock, Gauge, Hash, ChevronUp, ChevronDown, FileText, Pencil } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../components/ui/card';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { StatusFilter } from '../../../../../components/ui/StatusFilter';
import { DateRangePicker } from '../../../../../components/ui/date-range-picker';
import { CollapsibleFilterBar } from '../materials/tabs/Filters/CollapsibleFilterBar';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { WeldPassList } from '../../../../../components/WeldPassList';
import { NdtRepairLogModal } from './NDTIndicationLog/NdtRepairLogModal';
import { useWeldData, useCurrentProject, useWeldPasses, useZoneView, useTiltView } from '../../../../../redux/WeldData/hooks/useWeldData';
import { useProjectsLoader } from '../../../../../redux/WeldData/hooks/useWeldData';
import { updateWeldNumber } from '../../../../../redux/WeldData/actions';
import type { WeldData as WeldDataTableWeldData } from '../../../../../components/WeldDataTable';
import { UpdateWeldNumberDialog } from './UpdateWeldNumberDialog';
import { logDataHelperFunctions } from '../../../../../helpers/logdata-helper';
import { formatDuration } from '../../../../../lib/formatUtils';
import { formatTimestamp } from '../../../../../lib/formatUtils';
import { getWeldStatusCalculationMethod } from '../../../../../utils/statusUtils';
import type { DashboardProject } from '../../../types/project';
import { ProductionMap } from './ProjectProduction';
import { buildSelectedWeldPayload, buildWeldJobEntries, type WeldJobValue } from './weldSelection';

const STATUS_CALCULATION_MIN_DELAY_MS = 25000;
const STATUS_CALCULATION_DELAY_PER_WELD_MS = 1000;

interface WeldPass {
  name: string;
  status: 'Completed' | 'Not Started' | 'In Progress' | 'Rejected';
  welderId: string;
  duration: string;
  startTime?: string;
  endTime?: string;
  voltage?: { min: number; avg: number; max: number };
  current?: { min: number; avg: number; max: number };
  travelSpeed?: { min: number; avg: number; max: number };
  heat?: { min: number; avg: number; max: number };
  trueEnergy?: { min: number; avg: number; max: number };
  oscillationWidth?: { min: number; avg: number; max: number };
  wireSpeed?: { min: number; avg: number; max: number };
}

export interface WeldData {
  id: number;
  projectId: number;
  projectName: string;
  weldNumber: string;
  wpsNumber?: string;
  jobNumber?: string[]; // Add job_number field from API
  statusByInstantaneous: string;
  statusByAvgZone: string;
  statusByAvgPass: string;
  statusByAvgTilt: string;
  instantaneous_status?: string;
  start_time: number;
  end_time: number;
  weld_time: number;
  gps: any;
  min_distance: number | null;
  num_of_gps_points: number | null;
  pipe_diameter?: number; // Add pipe_diameter field from weld-status API
  wall_thickness?: number; // Add wall_thickness field from weld-status API
  pipeDiameter?: number; // Alternative field name
  diameter?: number; // Alternative field name
  wallThickness?: number; // Alternative field name
  thickness?: number; // Alternative field name
  passes?: WeldPass[];
  // Computed fields for display
  startDate?: string;
  endDate?: string;
  status?: 'Completed' | 'In Progress' | 'Not Started' | 'Rejected';
}

interface WeldDataMainTableProps {
  projectId?: number;
  projectName?: string;
  project?: DashboardProject; // Add project prop for map view
  selectedPipeSize?: number; // Add selectedPipeSize prop for map view
  onFilteredDataChange?: (filteredWpsNumbers: string[], selectedWeldNumbers?: string[], selectedWeldAndJobNumbers?: string[]) => void;
  onGenerateReport?: () => void; // Add prop to open report generation modal
  // Removed onData360ProjectIdChange prop - no longer needed
  selectedPipe?: any; // Add selected pipe prop for filtering
  statusCalculationMethod?: string; // Add status calculation method prop
  statusCalculationStartedAt?: number | null;
  filteredWeldIds?: string[]; // Add prop for filtering by specific weld IDs
  viewMode?: 'table' | 'map'; // Add view mode prop
  canReport?: boolean; // Add canReport prop for permission-based visibility
  canEdit?: boolean; // Add canEdit prop for permission-based visibility
}

export const WeldDataMainTable: React.FC<WeldDataMainTableProps> = ({
  projectId,
  projectName,
  project,
  selectedPipeSize,
  onFilteredDataChange,
  onGenerateReport,
  selectedPipe,
  statusCalculationMethod: propStatusCalculationMethod = 'Instantaneous',
  statusCalculationStartedAt = null,
  filteredWeldIds = [],
  viewMode = 'table',
  canReport = true,
  canEdit = true
}) => {

  const [selectedWeld, setSelectedWeld] = useState<WeldDataTableWeldData | null>(null);
  // Removed data360ProjectId state - now using actual project prop directly
  // Use the status calculation method from props
  const statusCalculationMethod = propStatusCalculationMethod;

  // State for weld number update modal
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [weldToUpdate, setWeldToUpdate] = useState<WeldData | null>(null);

  // State for NDT Repair Log modal
  const [selectedWeldForNdtRepairLog, setSelectedWeldForNdtRepairLog] = useState<WeldDataTableWeldData | null>(null);
  const [isStatusCalculationPending, setIsStatusCalculationPending] = useState(false);

  const dispatch = useDispatch();

  // Get update weld number loading state from Redux
  const updateWeldNumberLoading = useSelector((state: any) => state.weldData?.updateWeldNumberLoading || false);
  const updateWeldNumberSuccess = useSelector((state: any) => state.weldData?.updateWeldNumberSuccess || false);

  // Status calculation method is now passed as a prop from ProjectProduction
  const { weldData, loading, error, loadWeldData } = useWeldData();
  const { loadWeldPasses } = useWeldPasses();
  const { loadZoneView } = useZoneView();
  const { loadTiltView } = useTiltView();
  const { currentProject } = useCurrentProject();
  // Removed projects loading - no longer needed

  // Function to get the correct status based on calculation method
  const getWeldStatus = (weld: WeldData) => {
    const status = getWeldStatusCalculationMethod(statusCalculationMethod, weld);
    return status;
  };

  // Status calculation method is now passed as a prop from ProjectProduction

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof WeldData | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });

  // Filter states - all cleared by default
  const [searchQuery, setSearchQuery] = useState('');
  const [wpsFilter, setWpsFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });

  // Selection state for checkboxes
  const [selectedWeldNumbers, setSelectedWeldNumbers] = useState<string[]>([]);
  const [selectedWeldAndJobNumbers, setSelectedWeldAndJobNumbers] = useState<string[]>([]);
  
  // Ref to store latest filteredWpsNumbers for use in callbacks
  const filteredWpsNumbersRef = useRef<string[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);



  // Function to get current project details from actual project prop
  const getCurrentProjectDetails = useCallback(() => {
    return {
      projectId: projectId,
      projectName: projectName,
      isProject: !!projectId
    };
  }, [projectId, projectName]);



  const fetchWeldData = () => {
    if (currentProject?.project?.projectId) {
      // Use centralized project data - no need to manually find and set project details
      // Extract job number array and pipeId from selectedPipe
      const jobNumber = selectedPipe?.jobNumber?.jobNumbers || null;
      const pipeId = selectedPipe?.pipeId || null;
      loadWeldData(jobNumber, pipeId);
    }
  }



  // Load weld data when component mounts or when current project changes
  React.useEffect(() => {
    if (currentProject?.project?.projectId) {
      // Use centralized project data - no need to manually find and set project details
      // Extract job number array and pipeId from selectedPipe
      const jobNumber = selectedPipe?.jobNumber?.jobNumbers || null;
      const pipeId = selectedPipe?.pipeId || null;
      loadWeldData(jobNumber, pipeId);
    }
  }, [currentProject, loadWeldData, selectedPipe?.jobNumber]);

  // Removed onData360ProjectIdChange notification - no longer needed



  const handleOpenWeldPasses = (weld: WeldData) => {
    // Use centralized project data - no need to manually pass project details
    loadWeldPasses(weld.weldNumber);
    loadZoneView(weld.weldNumber);
    loadTiltView(weld.weldNumber);

    // Convert to the format expected by WeldPassList
    const weldForPassList: WeldDataTableWeldData = {
      id: weld.id,
      weldNumber: weld.weldNumber, // Use weld number as-is
      weldIdentifier: weld.weldNumber, // Use weld number as identifier
      wpsNumber: weld.wpsNumber, // Pass WPS number from job_number
      status: weld.status || 'Not Started',
      weldTime: weld.weld_time ? logDataHelperFunctions.formatWeldDuration(weld.weld_time) : '-',
      startTime: weld.start_time,
      endTime: weld.end_time,
      projectId: weld.projectId,
      projectName: weld.projectName,
      statusByInstantaneous: weld.statusByInstantaneous,
      statusByAvgZone: weld.statusByAvgZone,
      statusByAvgPass: weld.statusByAvgPass,
      statusByAvgTilt: weld.statusByAvgTilt,
      gps: weld.gps,
      minDistance: weld.min_distance,
      numOfGpsPoints: weld.num_of_gps_points,
      passes: weld.passes
    };

    setSelectedWeld(weldForPassList);
  };

  const handleCloseWeldPasses = () => {
    setSelectedWeld(null);
  };

  const handleOpenNtdRepairLog = (weld: WeldData) => {
    // Convert to the format expected by NdtRepairLogModal
    const weldForNdtRepairLog: WeldDataTableWeldData = {
      id: weld.id,
      weldNumber: weld.weldNumber,
      weldIdentifier: weld.weldNumber,
      wpsNumber: weld.wpsNumber,
      status: weld.status || 'Not Started',
      weldTime: weld.weld_time ? logDataHelperFunctions.formatWeldDuration(weld.weld_time) : '-',
      startTime: weld.start_time,
      endTime: weld.end_time,
      projectId: weld.projectId,
      projectName: weld.projectName,
      statusByInstantaneous: weld.statusByInstantaneous,
      statusByAvgZone: weld.statusByAvgZone,
      statusByAvgPass: weld.statusByAvgPass,
      statusByAvgTilt: weld.statusByAvgTilt,
      gps: weld.gps,
      minDistance: weld.min_distance,
      numOfGpsPoints: weld.num_of_gps_points,
      passes: weld.passes,
      pipeDiameter: weld.pipe_diameter,
      selectedPipe: selectedPipe,
    };

    setSelectedWeldForNdtRepairLog(weldForNdtRepairLog);
  };

  const handleCloseNtdRepairLog = () => {
    setSelectedWeldForNdtRepairLog(null);
  };

  const handleOpenUpdateModal = (weld: WeldData) => {
    setWeldToUpdate(weld);
    setUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    // Only allow closing if not currently loading
    if (!updateWeldNumberLoading) {
      setUpdateModalOpen(false);
      setWeldToUpdate(null);
    }
  };

  // Close modal on successful update
  useEffect(() => {
    if (updateWeldNumberSuccess) {
      //console.log('updateWeldNumberSuccess', updateWeldNumberSuccess);
      handleCloseUpdateModal();
      setTimeout(() => {

        fetchWeldData();
      }, 2000);
    }
  }, [updateWeldNumberSuccess]);

  const handleDateRangeUpdate = useCallback((values: {
    dateFrom: Date | undefined;
    dateTo: Date | undefined;
    compareFrom: Date | undefined;
    compareTo: Date | undefined;
  }) => {
    setDateRange({
      dateFrom: values.dateFrom,
      dateTo: values.dateTo
    });
  }, []);

  const handleSelectWeld = useCallback((weldNumber: string, jobNumber: WeldJobValue, checked: boolean) => {
  //  console.log('🔄 WeldDataMainTable - handleSelectWeld', weldNumber, jobNumber, checked);
    if (checked) {
      setSelectedWeldNumbers(prev => {
        const updated = Array.from(new Set([...prev, weldNumber]));
        const newWeldJobEntries = buildWeldJobEntries(weldNumber, jobNumber);
        
        setSelectedWeldAndJobNumbers(prevJob => {
          const updatedJob = Array.from(new Set([...prevJob, ...newWeldJobEntries]));
          // Notify parent of the updated selections
          if (onFilteredDataChange) {
            onFilteredDataChange(filteredWpsNumbersRef.current, updated, updatedJob);
          }
          return updatedJob;
        });
        return updated;
      });
    } else {
      setSelectedWeldNumbers(prev => {
        const updated = prev.filter(num => num !== weldNumber);
        // Remove all entries for this weld (handles comma-separated jobs: 20:PL-AS-HA-BD-125, 20:Simulation)
        setSelectedWeldAndJobNumbers(prevJob => {
          const updatedJob = prevJob.filter(entry => !entry.startsWith(`${weldNumber}:`));
          // Notify parent of the updated selections
          if (onFilteredDataChange) {
            onFilteredDataChange(filteredWpsNumbersRef.current, updated, updatedJob);
          }
          return updatedJob;
        });
        return updated;
      });
    }
  }, [onFilteredDataChange]);

  // Process API data to match our interface - Memoized for performance
  const rawDisplayData = useMemo(() => {
    if (weldData.length === 0) return [];


    return weldData.map((item: any) => ({
      id: item.id,
      projectId: item.projectId,
      projectName: item.projectName,
      weldNumber: item.weldNumber,
      wpsNumber: item.job_number,
      jobNumber: item.job_number,
      // Map both old and new field names for status calculation
      statusByInstantaneous: item.statusByInstantaneous,
      statusByAvgZone: item.statusByAvgZone,
      statusByAvgPass: item.statusByAvgPass,
      statusByAvgTilt: item.statusByAvgTilt,
      // Add new field names from pass data
      pass_status_by_instantaneous: item.pass_status_by_instantaneous,
      pass_avg_status: item.pass_avg_status,
      pass_staus_by_zone: item.pass_staus_by_zone,
      instantaneous_status: item.instantaneous_status,
      start_time: item.startTime,
      end_time: item.endTime,
      weld_time: (() => {
        const raw = item.weldTime;
        if (raw == null || raw === '-') return 0;
        const str = String(raw).replace(/\s*mins?/i, '').trim();
        const n = parseFloat(str);
        return Number.isFinite(n) ? n : 0;
      })(),
      gps: item.gps,
      min_distance: item.minDistance,
      num_of_gps_points: item.numOfGpsPoints,
      pipe_diameter: item.pipe_diameter, // Add pipe_diameter field
      wall_thickness: item.wall_thickness, // Add wall_thickness field
      startDate: item.startTime,
      endDate: item.endTime,
      status: item.status
    }));
  }, [weldData]);

  // Sorting function
  const handleSort = (key: keyof WeldData) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon
  const getSortIcon = (key: keyof WeldData) => {
    if (sortConfig.key !== key) {
      return <div className="h-4 w-4 text-heritage-grey" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-4 w-4" /> :
      <ChevronDown className="h-4 w-4" />;
  };

  const getWelDataList = () => {
    if (!selectedPipe)
      return rawDisplayData;
    let filtered = rawDisplayData.filter((weld: WeldData) => {
      // For other projects, show all data without pipe size filtering
      const selectedPipeSize = selectedPipe?.size || selectedPipe?.pipeSize || selectedPipe?.diameter;
      let shouldApplyPipeFilter = selectedPipe && selectedPipeSize &&
        (weld.projectId === 270 || weld.projectName === "Golfinho 8\"");

      shouldApplyPipeFilter = selectedPipe && selectedPipeSize &&
        (weld.projectId === 322 || weld.projectName === "Ruya");
      // console.log('🔍 WeldDataMainTable - shouldApplyPipeFilter:', shouldApplyPipeFilter,selectedPipeSize);
      if (shouldApplyPipeFilter) {
        // Check if weld has pipe_diameter field from the API
        // Also check for alternative field names that might be used
        const weldPipeDiameter = weld.pipe_diameter || weld.pipeDiameter || weld.diameter;
        //  console.log('🔍 WeldDataMainTable - weldPipeDiameter:', weldPipeDiameter);


        // Check if pipe diameter exists and has valid value
        if (weldPipeDiameter !== undefined && weldPipeDiameter !== null) {
          // Convert to numbers for comparison (in case they come as strings from API)
          const weldPipeDiameterNum = Number(weldPipeDiameter);
          const selectedSizeNum = Number(selectedPipeSize);
          // Compare pipe diameter - truncate decimal values to match integer pipe sizes
          const pipeDiameterMatch = Math.floor(Math.abs(weldPipeDiameterNum)) === selectedSizeNum;

          if (!pipeDiameterMatch) {
            return false;
          }

          // Skip wall thickness filtering for now - only filter by pipe diameter
          // Wall thickness filtering can be added later if needed

        } else {
          return false;
        }
      }

      return true;
    });

    return filtered;
  }

  // Apply filters and sorting - Optimized with pagination
  const { filteredData, paginatedData, totalPages, totalItems } = useMemo(() => {

    let filtered = getWelDataList().filter((weld: WeldData) => {

      // Weld ID filter - filter by specific weld IDs if provided
      if (filteredWeldIds.length > 0) {
        if (!filteredWeldIds.includes(weld.weldNumber)) {
          return false;
        }
      }

      // Search filter - search in weld number only
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const weldNumberMatch = weld.weldNumber.toLowerCase().includes(searchLower);

        if (!weldNumberMatch) {
          return false;
        }
      }

      // WPS filter - separate filter for WPS number
      if (wpsFilter.length > 0) {
        if (!weld.wpsNumber || !wpsFilter.includes(weld.wpsNumber)) {
          return false;
        }
      }

      // Status filter
      if (statusFilter.length > 0) {
        const status = getWeldStatus(weld);
        if (!status || !statusFilter.includes(status)) {
          return false;
        }
      }

      // Date range filter
      if (dateRange.dateFrom || dateRange.dateTo) {
        const weldStartDate = weld.start_time ? new Date(weld.start_time * 1000) : null;

        if (weldStartDate) {
          if (dateRange.dateFrom) {
            // Set time to start of day for comparison
            const fromDate = new Date(dateRange.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            const weldDate = new Date(weldStartDate);
            weldDate.setHours(0, 0, 0, 0);

            if (weldDate < fromDate) {
              return false;
            }
          }
          if (dateRange.dateTo) {
            // Set time to end of day for comparison (23:59:59.999)
            const toDate = new Date(dateRange.dateTo);
            toDate.setHours(23, 59, 59, 999);
            const weldDate = new Date(weldStartDate);

            if (weldDate > toDate) {
              return false;
            }
          }
        }
      }

      // Pipe size filter - ONLY apply for specific projects (ID 270 or name "Golfinho 8"")
      // For other projects, show all data without pipe size filtering
      const selectedPipeSize = selectedPipe?.size || selectedPipe?.pipeSize || selectedPipe?.diameter;
      let shouldApplyPipeFilter = selectedPipe && selectedPipeSize &&
        (weld.projectId === 270 || weld.projectName === "Golfinho");

      if (!shouldApplyPipeFilter && weld.projectName === "Ruya")
        shouldApplyPipeFilter = selectedPipe && selectedPipeSize && (weld.projectId === 322 || weld.projectName === "Ruya");
      // console.log('🔍 WeldDataMainTable - shouldApplyPipeFilter:', shouldApplyPipeFilter,selectedPipeSize);
      if (shouldApplyPipeFilter) {
        // Check if weld has pipe_diameter field from the API
        // Also check for alternative field names that might be used
        const weldPipeDiameter = weld.pipe_diameter || weld.pipeDiameter || weld.diameter;
        //  console.log('🔍 WeldDataMainTable - weldPipeDiameter:', weldPipeDiameter);


        // Check if pipe diameter exists and has valid value
        if (weldPipeDiameter !== undefined && weldPipeDiameter !== null) {
          // Convert to numbers for comparison (in case they come as strings from API)
          const weldPipeDiameterNum = Number(weldPipeDiameter);
          const selectedSizeNum = Number(selectedPipeSize);
          // Compare pipe diameter - truncate decimal values to match integer pipe sizes
          const pipeDiameterMatch = Math.floor(Math.abs(weldPipeDiameterNum)) === selectedSizeNum;

          if (!pipeDiameterMatch) {
            return false;
          }

          // Skip wall thickness filtering for now - only filter by pipe diameter
          // Wall thickness filtering can be added later if needed

        } else {
          return false;
        }
      }

      return true;
    });

    // Get selected pipe size for debugging
    const selectedPipeSize = selectedPipe?.size || selectedPipe?.pipeSize || selectedPipe?.diameter;
    const shouldApplyPipeFilter = selectedPipe && selectedPipeSize &&
      (rawDisplayData.some((weld: WeldData) => weld.projectId === 270 || weld.projectName === "Golfinho"));

    // Count welds by pipe diameter in ALL data (before filtering)
    const allPipeDiameterCounts = rawDisplayData.reduce((acc: Record<number, number>, weld: WeldData) => {
      const diameter = weld.pipe_diameter || weld.pipeDiameter || weld.diameter;
      if (diameter !== undefined && diameter !== null) {
        acc[diameter] = (acc[diameter] || 0) + 1;
      }
      return acc;
    }, {} as Record<number, number>);

    // Count welds by pipe diameter in the filtered results
    const pipeDiameterCounts = filtered.reduce((acc: Record<number, number>, weld: WeldData) => {
      const diameter = weld.pipe_diameter || weld.pipeDiameter || weld.diameter;
      if (diameter !== undefined && diameter !== null) {
        acc[diameter] = (acc[diameter] || 0) + 1;
      }
      return acc;
    }, {} as Record<number, number>);


    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a: WeldData, b: WeldData) => {
        let aValue = a[sortConfig.key!];
        let bValue = b[sortConfig.key!];

        // Handle status field specially
        if (sortConfig.key === 'statusByInstantaneous') {
          aValue = getWeldStatus(a);
          bValue = getWeldStatus(b);
        }

        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const comparison = aValue - bValue;
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        return 0;
      });
    }

    // Apply pagination
    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);


    return {
      filteredData: filtered,
      paginatedData: paginated,
      totalPages,
      totalItems
    };
  }, [rawDisplayData, searchQuery, wpsFilter, statusFilter, dateRange, sortConfig, currentPage, itemsPerPage, selectedPipe, filteredWeldIds]);

  // Use paginated data for display
  const displayData = paginatedData;

  useEffect(() => {
    if (!statusCalculationStartedAt) {
      setIsStatusCalculationPending(false);
      return;
    }

    const weldCount = Math.max(filteredData.length, 1);
    const calculationDelay = Math.max(
      STATUS_CALCULATION_MIN_DELAY_MS,
      weldCount * STATUS_CALCULATION_DELAY_PER_WELD_MS,
    );
    const remainingDelay = calculationDelay - (Date.now() - statusCalculationStartedAt);

    if (remainingDelay <= 0) {
      setIsStatusCalculationPending(false);
      return;
    }

    setIsStatusCalculationPending(true);

    const timer = setTimeout(() => {
      fetchWeldData();
      setIsStatusCalculationPending(false);
    }, remainingDelay);

    return () => clearTimeout(timer);
  }, [statusCalculationStartedAt, filteredData.length]);

  const renderWeldStatusIcon = (weld: WeldData) => {
    if (isStatusCalculationPending) {
      return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
    }

    const status = getWeldStatus(weld);
    if (status === 'TRUE') {
      return <ThumbsUp className="h-5 w-5 text-galvanizing-green" />;
    }
    if (status === 'FALSE') {
      return <ThumbsDown className="h-5 w-5 text-hero-red" />;
    }
    if (status === 'EX') {
      return <span className="h-5 w-5 flex items-center justify-center font-bold text-lg bg-yellow-400 text-black rounded-full">!</span>;
    }
    if (status === 'CROSS') {
      return <span className="h-5 w-5 flex items-center justify-center font-bold text-lg text-hero-red">×</span>;
    }
    if (status === 'NULL' || status === null || status === undefined) {
      return <span className="text-heritage-grey text-lg">-</span>;
    }
    return <div className="h-5 w-5 rounded-full bg-yellow-400 animate-pulse" />;
  };

  const selectedPipeSelectionKey = useMemo(() => {
    const jobNumbers = selectedPipe?.jobNumber?.jobNumbers;
    const normalizedJobNumbers = Array.isArray(jobNumbers) ? jobNumbers.join('|') : jobNumbers ?? '';

    return [
      selectedPipe?.pipeId ?? '',
      selectedPipe?.displayName ?? '',
      normalizedJobNumbers,
    ].join('|');
  }, [selectedPipe]);

  useEffect(() => {
    setSelectedWeldNumbers([]);
    setSelectedWeldAndJobNumbers([]);
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredWpsNumbersRef.current, [], []);
    }
  }, [selectedPipeSelectionKey, onFilteredDataChange]);

  // Selection handlers that depend on displayData
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      // Select all welds on current page
      const {
        selectedWeldNumbers: currentPageWeldNumbers,
        selectedWeldAndJobNumbers: currentPageWeldAndJobNumbers,
      } = buildSelectedWeldPayload(displayData);
      setSelectedWeldNumbers(currentPageWeldNumbers);
      setSelectedWeldAndJobNumbers(currentPageWeldAndJobNumbers);
      if (onFilteredDataChange) {
        onFilteredDataChange(filteredWpsNumbersRef.current, currentPageWeldNumbers, currentPageWeldAndJobNumbers);
      }
    } else {
      setSelectedWeldNumbers([]);
      setSelectedWeldAndJobNumbers([]);
      if (onFilteredDataChange) {
        onFilteredDataChange(filteredWpsNumbersRef.current, [], []);
      }
    }
  }, [displayData, onFilteredDataChange]);

  const isAllSelected = displayData.length > 0 && displayData.every((weld: WeldData) => selectedWeldNumbers.includes(weld.weldNumber));
  const isSomeSelected = displayData.some((weld: WeldData) => selectedWeldNumbers.includes(weld.weldNumber)) && !isAllSelected;

  // Check if there are active filters
  // Note: Pipe size is not considered a filter - it's part of the data specification
  const hasActiveFilters = searchQuery !== '' || wpsFilter.length > 0 || statusFilter.length > 0 || dateRange.dateFrom !== undefined || dateRange.dateTo !== undefined;

  // Memoize the filtered weld IDs to prevent unnecessary recalculations
  const filteredWpsNumbers = useMemo(() => {
    // Only return weld IDs if filters are actually applied
    if (!hasActiveFilters) {
      return []; // Return empty array when no filters are applied
    }

    // Return weld IDs only when filters are applied (keeping parameter name as weldNumbers)
    return filteredData.map((weld: WeldData) => weld.weldNumber.toString()).filter((weldId: string) => Boolean(weldId));
  }, [filteredData, hasActiveFilters]);

  // Update ref whenever filteredWpsNumbers changes
  useEffect(() => {
    filteredWpsNumbersRef.current = filteredWpsNumbers;
  }, [filteredWpsNumbers]);

  // Continuous emission removed — was causing heavy re-renders that interfered
  // with tab navigation. Parent-level actions should trigger emission on demand
  // via an explicit handler rather than relying on effect propagation.

  // Handle clearing all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setWpsFilter([]);
    setStatusFilter([]);
    setDateRange({ dateFrom: undefined, dateTo: undefined });
    setSelectedWeldNumbers([]); // Clear selections
    setSelectedWeldAndJobNumbers([]); // Clear weld and job number selections
    setCurrentPage(1); // Reset to first page when clearing filters
    // Notify parent that selections are cleared
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredWpsNumbers, [], []);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, wpsFilter, statusFilter, dateRange]);

  // If map view is selected, render the ProductionMap component
  if (viewMode === 'map' && project && selectedPipeSize !== undefined) {
    return (
      <div className="w-full h-full">
        <ProductionMap
          project={project}
          selectedPipeSize={selectedPipeSize}
          selectedPipe={selectedPipe}
          statusCalculationMethod={statusCalculationMethod}
        />
      </div>
    );
  }

  const activeFilterCount =
    (wpsFilter.length > 0 ? 1 : 0) +
    (statusFilter.length > 0 ? 1 : 0) +
    (dateRange.dateFrom !== undefined || dateRange.dateTo !== undefined ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Collapsible Filters */}
      <div>
        <CollapsibleFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search weld number..."
          activeFilterCount={activeFilterCount}
          filteredCount={filteredData.length}
          filteredCountLabel="welds"
          onResetFilters={handleClearFilters}
        >
          <MultiSelect
            options={Array.from(new Set(getWelDataList().map((weld: WeldData) => weld.wpsNumber).filter(Boolean))).map((wpsNumber) => ({
              value: wpsNumber as string,
              label: wpsNumber as string,
            }))}
            selected={wpsFilter}
            onChange={setWpsFilter}
            placeholder="WPS"
          />
          <StatusFilter
            selected={statusFilter}
            onChange={setStatusFilter}
            placeholder="Status"
          />
          <DateRangePicker
            initialDateFrom={dateRange.dateFrom}
            initialDateTo={dateRange.dateTo}
            onUpdate={handleDateRangeUpdate}
            showCompare={false}
          />
        </CollapsibleFilterBar>

        {/* Inline Clear Selection chip when welds are selected */}
        {selectedWeldNumbers.length > 0 && (
          <div className="mt-2 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedWeldNumbers([]);
                setSelectedWeldAndJobNumbers([]);
                if (onFilteredDataChange) {
                  onFilteredDataChange(filteredWpsNumbers, [], []);
                }
              }}
              className="h-8 text-[12px] text-readable-red border-[#E2E5EA] hover:bg-[rgba(235,28,55,0.08)] flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              Clear selection ({selectedWeldNumbers.length})
            </Button>
          </div>
        )}
      </div>
      {/* Responsive Table Container */}
      <Card>
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
        {/* Mobile Card View */}
        <div className="block lg:hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red"></div>
                  <span className="text-heritage-grey text-lg">Loading weld data...</span>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-hero-red">
              Error loading weld data: {error}
            </div>
          ) : displayData.length === 0 ? (
            <div className="p-8 text-center text-heritage-grey">
              No weld data available
            </div>
          ) : (
            <div className="divide-y divide-[#EEF0F3]">
              {displayData.map((weld: WeldData, index: number) => (
                <div key={weld.id} className="p-4 hover:bg-page-bg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedWeldNumbers.includes(weld.weldNumber)}
                        onCheckedChange={(checked) => handleSelectWeld(weld.weldNumber, weld.wpsNumber || '', checked === true)}
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer"
                        title={`Select weld ${weld.weldNumber}`}
                      />
                      <span className="text-sm font-medium text-heritage-grey">#{index + 1}</span>
                      <span className="font-semibold text-blueprint-blue">{weld.weldNumber}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-page-bg p-1 h-6 w-6"
                        title="Edit Weld Number"
                        onClick={() => handleOpenUpdateModal(weld)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      {renderWeldStatusIcon(weld)}
                      {selectedPipe.isWeldingEnabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-page-bg"
                        title="View Weld Passes"
                        onClick={() => handleOpenWeldPasses(weld)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      )}
                    {selectedPipe.NDT && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-page-bg"
                        title="View NDT Data"
                        onClick={() => handleOpenNtdRepairLog(weld)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-heritage-grey">WPS Number:</span>
                      <div className="font-medium">{weld.wpsNumber || '-'}</div>
                    </div>
                    <div>
                      <span className="text-heritage-grey">Duration:</span>
                      <div className="font-medium">{weld.weld_time ? formatDuration(weld.weld_time) : '-'}</div>
                    </div>
                    <div>
                      <span className="text-heritage-grey">Start Date:</span>
                      <div className="font-medium">{weld.startDate ? formatTimestamp(Number(weld.startDate)) : '-'}</div>
                    </div>
                    <div>
                      <span className="text-heritage-grey">End Date:</span>
                      <div className="font-medium">{weld.endDate ? formatTimestamp(Number(weld.endDate)) : '-'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block">
          {/* Only the weld rows scroll; the header sticks to the top of this region. */}
          <div>
            <Table className="min-w-[930px]" containerClassName="">
              <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                      <TableHead
                        className="text-center w-[50px]"
                        style={{ textAlign: 'center' }}
                      >
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAllSelected}
                            ref={(el) => {
                              if (el) {
                                (el as any).indeterminate = isSomeSelected;
                              }
                            }}
                            onCheckedChange={(checked) => handleSelectAll(checked === true)}
                            className="cursor-pointer"
                            title={isAllSelected ? "Deselect all" : "Select all"}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-center w-[60px] cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                        onClick={() => handleSort('id')}
                        style={{ textAlign: 'center' }}
                      >
                        <div className="flex items-center justify-center">
                          <Hash className="h-3 w-3" />
                          Sl.no
                          {getSortIcon('id')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-center w-[120px] cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                        onClick={() => handleSort('weldNumber')}
                        style={{ textAlign: 'center' }}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Target className="h-4 w-4" />
                          Weld ID
                          {getSortIcon('weldNumber')}
                        </div>
                      </TableHead>

                      <TableHead
                        className="text-left w-[180px] cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                        onClick={() => handleSort('wpsNumber')}
                      >
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          Job Number
                          {getSortIcon('wpsNumber')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-center w-[70px] cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                        onClick={() => handleSort('statusByInstantaneous')}
                        style={{ textAlign: 'center' }}
                      >
                        <div className="flex items-center justify-center">
                          <Activity className="h-3 w-3" />
                          Status
                          {getSortIcon('statusByInstantaneous')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-center w-[200px] cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                        onClick={() => handleSort('start_time')}
                        style={{ textAlign: 'center' }}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="h-3 w-3" />
                          Start → End
                          {getSortIcon('start_time')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-center w-[90px] cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                        onClick={() => handleSort('weld_time')}
                        style={{ textAlign: 'center' }}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <Gauge className="h-3 w-3" />
                          Duration
                          {getSortIcon('weld_time')}
                        </div>
                      </TableHead>
                      {selectedPipe.isWeldingEnabled && <TableHead className="text-center w-[80px]" style={{ textAlign: 'center' }}>
                        <div className="flex items-center justify-center gap-2" title="Action column - not sortable">
                          <Eye className="h-4 w-4" />
                          Weld Data
                        </div>
                      </TableHead>
                      }
                      {selectedPipe.NDT && <TableHead className="text-center w-[80px]" style={{ textAlign: 'center' }}>
                        <div className="flex items-center justify-center gap-2" title="Action column - not sortable">
                          <Eye className="h-4 w-4" />
                          NDT Data
                        </div>
                      </TableHead>
                      }
                    </TableRow>
              </TableHeader>
              <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8">
                            <div className="flex items-center justify-center">
                              <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red"></div>
                                <span className="text-heritage-grey text-lg">Loading weld data...</span>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : error ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-hero-red">
                            Error loading weld data: {error}
                          </TableCell>
                        </TableRow>
                      ) : displayData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-heritage-grey">
                            No weld data available
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayData.map((weld: WeldData, index: number) => (
                          <TableRow key={weld.id}>
                            <TableCell className="text-center w-[50px]" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={selectedWeldNumbers.includes(weld.weldNumber)}
                                  onCheckedChange={(checked) => handleSelectWeld(weld.weldNumber, weld.wpsNumber || '', checked === true)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="cursor-pointer"
                                  title={`Select weld ${weld.weldNumber}`}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-center w-[60px] font-medium" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center">{index + 1}</div>
                            </TableCell>
                            <TableCell className="text-center w-[120px] font-medium" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center gap-2">
                                <span>{weld.weldNumber}</span>
                                {canEdit && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="hover:bg-page-bg"
                                    title="Edit Weld Number"
                                    onClick={() => handleOpenUpdateModal(weld)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>

                            <TableCell className="text-left w-[180px]">
                              <span className="truncate block" title={weld.wpsNumber || ''}>{weld.wpsNumber || '-'}</span>
                            </TableCell>
                            <TableCell className="text-center w-[70px]" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center">
                                {renderWeldStatusIcon(weld)}
                              </div>
                            </TableCell>
                            <TableCell className="text-center w-[200px]" style={{ textAlign: 'center' }}>
                              {(() => {
                                if (!weld.startDate && !weld.endDate) return <span>-</span>;
                                const startStr = weld.startDate ? formatTimestamp(Number(weld.startDate)) : '-';
                                const endStr = weld.endDate ? formatTimestamp(Number(weld.endDate)) : '-';
                                // formatTimestamp returns "02-Sep-2025, 10:32 AM" — split at ", " to get [date, time]
                                const [startDate, startTime] = startStr.includes(', ') ? startStr.split(', ') : [startStr, ''];
                                const [endDate, endTime] = endStr.includes(', ') ? endStr.split(', ') : [endStr, ''];
                                if (startDate === endDate && startTime && endTime) {
                                  return (
                                    <div className="leading-tight">
                                      <div>{startDate}</div>
                                      <div className="text-heritage-grey">{startTime} → {endTime}</div>
                                    </div>
                                  );
                                }
                                return (
                                  <div className="leading-tight">
                                    <div>{startStr}</div>
                                    <div className="text-heritage-grey">→ {endStr}</div>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-center w-[90px]" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center">
                                {weld.weld_time ? formatDuration(weld.weld_time) : '-'}
                              </div>
                            </TableCell>
                            {selectedPipe.isWeldingEnabled && <TableCell className="text-center w-[80px]" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-page-bg"
                                  title="View Weld Passes"
                                  onClick={() => handleOpenWeldPasses(weld)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                            }
                            {selectedPipe.NDT &&  <TableCell className="text-center w-[80px]" style={{ textAlign: 'center' }}>
                              <div className="flex items-center justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="hover:bg-page-bg"
                                  title="View NDT Data"
                                  onClick={() => handleOpenNtdRepairLog(weld)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>}
                          </TableRow>
                        ))
                      )}
              </TableBody>
            </Table>
          </div>
        </div>
            </div>
          </div>

        {/* Pagination footer (inside the same table card) */}
        <div className="pt-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end items-end gap-3 ">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-medium text-text-grey">Showing:</span>
                <span className="text-[12px] text-blueprint-blue font-semibold">
                  {totalItems > 0 ? `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalItems)}` : '0'} of {totalItems}
                </span>
              </div>
              {selectedWeldNumbers.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-medium text-text-grey">Selected:</span>
                  <span className="text-[12px] text-blueprint-blue font-semibold">
                    {selectedWeldNumbers.length} weld{selectedWeldNumbers.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-text-grey">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-[#E2E5EA] bg-white rounded-lg px-2 py-1 text-[12px] text-text-grey"
                  title="Select items per page"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="hidden md:inline-flex h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-white"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-white"
                >
                  Prev
                </Button>
                <span className="text-[12px] text-text-grey px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-white"
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="hidden md:inline-flex h-8 px-3 text-[12px] border-[#E2E5EA] text-text-grey hover:bg-white"
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        </div>
        </CardContent>
      </Card>

      {selectedWeld && (
        <WeldPassList
          isOpen={true}
          onClose={handleCloseWeldPasses}
          weld={selectedWeld}
          assemblyNumber="Main"
          statusCalculationMethod={statusCalculationMethod}
          passViewStatusMethod={statusCalculationMethod}
          zoneViewStatusMethod={statusCalculationMethod}
          tiltViewStatusMethod={statusCalculationMethod}
          statusCalculationStartedAt={statusCalculationStartedAt}
          canEdit={canEdit}
        />
      )}

      {/* NDT Repair Log Modal */}
      {selectedWeldForNdtRepairLog && (
        <NdtRepairLogModal
          isOpen={true}
          onClose={handleCloseNtdRepairLog}
          weld={selectedWeldForNdtRepairLog}
          canEdit={canEdit}
        />
      )}

      {/* Weld Number Update Modal */}
      <UpdateWeldNumberDialog
        open={updateModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseUpdateModal();
          }
        }}
        title="Update New Weld Number"
        currentValue={weldToUpdate?.weldNumber || ''}
        currentJobNumber={weldToUpdate?.wpsNumber || ''}
        showJobNumber={false}
        onConfirm={(newValue, newJobNumber) => {
          if (weldToUpdate && newValue && !updateWeldNumberLoading) {
            const currentProjectId = currentProject?.project?.projectId || projectId;
            const oldWeldNumber = weldToUpdate.weldNumber;
            const oldJobNumber = weldToUpdate.wpsNumber || '';

            // If weld number is the same, use previous value for both old and new
            const finalNewWeldNumber = newValue === oldWeldNumber ? oldWeldNumber : newValue;

            // If job number is the same or not provided, use previous value for both old and new
            const finalNewJobNumber = (!newJobNumber || newJobNumber === oldJobNumber) ? oldJobNumber : newJobNumber;

            const payload = {
              project_id: currentProjectId,
              old_weld_number: oldWeldNumber,
              new_weld_number: finalNewWeldNumber,
              oldJobNumber: oldJobNumber,
              newJobNumber: finalNewJobNumber,
              isNewWeld: true
            };
            dispatch(updateWeldNumber(payload));
          }
        }}
        loading={updateWeldNumberLoading}
        maxWidth="3xl"
      />
    </div>
  );
}; 